import { gql } from '@apollo/client';

export const GET_CURRENT_TENANT = gql`
  query GetCurrentTenant {
    getCurrentTenant {
      tenantId
      name
      onboardingStep
      additionalData
    }
  }
`;

export const GET_CURRENT_HOST = gql`
  query GetCurrentHost {
    getCurrentVisitor {
      id
      email
      isAdmin
      mobileDevices {
        id
        isRegistered
        registrationToken
        registrationDate
        role
      }
    }
  }
`;

export const GET_CURRENT_VISITOR = gql`
  query GetCurrentVisitor {
    getCurrentVisitor {
      id
      email
      isAdmin
      mobileDevices {
        id
        isRegistered
        registrationToken
        registrationDate
        role
      }
      identityProvider
    }
  }
`;

export const GET_DOORS_FOR_TENANT_WITH_OFFICE_MODE = gql`
  query GetDoorsForTenantWithOfficeMode {
    getDoorsForTenant {
      id
      name
      hardwareIdentifier
      batteryWarningLevel
      officeMode {
        ... on WeekSchedule {
          weekDays {
            dayName
            from
            to
          }
        }
      }
      type
    }
  }
`;

export const GET_DOORS_FOR_TENANT_WITH_LOCATIONS = gql`
  query GetDoorsForTenantWithLocations {
    getDoorsForTenant {
      id
      name
      locations {
        id
        name
      }
      hardwareIdentifier
      batteryWarningLevel
      officeMode {
        ... on WeekSchedule {
          weekDays {
            dayName
            from
            to
          }
        }
      }
      type
    }
  }
`;

export const GET_LOCATIONS_WITH_DOORS_FOR_TENANT = gql`
  query GetLocationsWithDoorsForTenant {
    getLocationsForTenant {
      id
      name
      doors {
        id
        name
        hardwareIdentifier
        batteryWarningLevel
        type
      }
    }
  }
`;

export const GET_VISITOR_GROUPS_FOR_TENANT = gql`
  query GetVisitorGroupsForTenant {
    getVisitorGroupsForTenant {
      id
      name
      externalRef
      accessGrants {
        id
        doors {
          id
          name
          hardwareIdentifier
          batteryWarningLevel
        }
      }
      type
      isQrCodeVisitInviterGroup
    }
  }
`;

export const GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS = gql`
  query GetVisitorGroupsForTenantWithMembers($filter: VisitorGroupsFilter) {
    getVisitorGroupsForTenant(filter: $filter) {
      id
      name
      members {
        id
        name
        email
        mobileDevices {
          id
          isRegistered
          registrationToken
          registrationDate
          role
        }
        isEditable
        expirationDate
      }
      type
      externalRef
    }
  }
`;

export const GET_ACCESS_EVENTS_FOR_TENANT = gql`
  query GetAccessEventsForTenant($nextToken: String, $filter: AccessEventsFilter) {
    getAccessEventsForTenant(nextToken: $nextToken, filter: $filter) {
      items {
        id
        createdAt
        visitor {
          id
          name
          email
        }
        door {
          id
          name
          type
        }
        action
        reason
      }
      nextToken
    }
  }
`;

export const GET_VISITOR_GROUPS_WITH_PERMISSIONS = gql`
  query GetVisitorGroupsForTenantWithIsAdminGroup($filter: VisitorGroupsFilter) {
    getVisitorGroupsForTenant(filter: $filter) {
      id
      name
      externalRef
      isAdminGroup
      isQrCodeVisitInviterGroup
    }
  }
`;

export const GET_VISITOR_GROUPS_FOR_TENANT_WITH_IS_OFFICE_MODE_ENABLED = gql`
  query GetVisitorGroupsForTenantWithIsOfficeModeEnabled {
    getVisitorGroupsForTenant {
      id
      name
      accessGrants {
        id
        isOfficeModeEnabled
      }
      externalRef
      type
    }
  }
`;

export const GET_VISITOR_GROUPS_FOR_TENANT_WITH_ACCESS_GRANTS = gql`
  query GetVisitorGroupsForTenantWithAccessGrants($filter: VisitorGroupsFilter) {
    getVisitorGroupsForTenant(filter: $filter) {
      id
      name
      accessGrants {
        id
        accessSchedule {
          ... on WeekSchedule {
            weekDays {
              dayName
              from
              to
            }
          }
        }
        doors {
          hardwareIdentifier
          id
          locations {
            id
            name
          }
          name
          batteryWarningLevel
          officeMode {
            ... on WeekSchedule {
              weekDays {
                dayName
                from
                to
              }
            }
          }
        }
        locations {
          id
          name
          doors {
            id
            name
            hardwareIdentifier
            batteryWarningLevel
          }
        }
      }
      externalRef
      type
    }
  }
`;

export const GET_TENANT_LEGAL_DOCUMENTS = gql`
  query GetTenantLegalDocuments {
    getTenantLegalDocuments {
      tenant {
        tenantId
        name
        onboardingStep
        additionalData
      }
      dpa {
        documentType
        currentVersion
        currentText
        id
        acceptedVersion
        acceptedOn
      }
      sla {
        documentType
        currentVersion
        currentText
        id
        acceptedVersion
        acceptedOn
      }
    }
  }
`;

export const GET_TENANT_REGISTRATION_INFO = gql`
  query GetTenantRegistrationInfo {
    getTenantRegistrationInfo {
      tenantId
      azureTenantId
      entraAdminConsentUrl
    }
  }
`;

export const GET_TENANT_NAME = gql`
  query GetTenantName($azureTenantId: String!) {
    getTenantName(azureTenantId: $azureTenantId)
  }
`;

export const GET_REST_API_KEYS = gql`
  query GetRestApiKeys {
    getRestApiKeys {
      apiKey
      createdAt
      description
      id
    }
  }
`;

export const GET_MFA_PREFERENCE = gql`
  query GetMfaPreference {
    getMfaPreference {
      emailMfaEnabled
    }
  }
`;

export const GET_CARDS = gql`
  query GetNfcCards {
    getNfcCards {
      uuid
      name
      updateRequired
      assignment {
        expiryDate
        expired
        blocked {
          since
          reason
        }
        visitor {
          id
          expirationDate
          name
        }
        visitorName
      }
    }
  }
`;

export const GET_ALL_VISITORS = gql`
  query GetAllVisitors {
    getVisitorGroupsForTenant {
      id
      members {
        id
        name
        email
        mobileDevices {
          id
          isRegistered
          role
        }
        identityProvider
        expirationDate
        visitorGroupMembership {
          visitorGroup {
            id
            name
            type
          }
          role
        }
        isAdmin
      }
      type
    }
  }
`;

export const GET_ALL_VISITORS_CARDS = gql`
  query GetAllVisitorsCards {
    getNfcCards {
      uuid
      name
      updateRequired
      assignment {
        expiryDate
        expired
        blocked {
          since
          reason
        }
        visitor {
          id
          expirationDate
          name
        }
        visitorName
      }
    }
  }
`;
